import { HStack, IconButton } from '@chakra-ui/react';

import { ADVISOR_PANEL_NEUTRAL_100, ADVISOR_PANEL_NEUTRAL_250, ADVISOR_PANEL_NEUTRAL_50_BG } from './advisor-panel-tokens';
import { KinThumbDownIcon, KinThumbUpIcon } from './kin-agent-icons';

export type AgentAssistantThumbsFeedbackProps = {
    rating: 'up' | 'down' | null | undefined;
  disabled?: boolean;
    accentColor: string;
  /** May resolve to ``false`` when feedback persistence fails (hook returns boolean). */
  onRate: (next: 'up' | 'down' | null) => void | boolean | Promise<void | boolean>;
};

const ICON_BTN_SIZE = '28px';
const ICON_SIZE = 16;

/**
 * Helpful / not helpful controls for a completed assistant turn (Kin Chatbot / partner advisor).
 * Idle state is borderless so the pair reads as a light toolbar under the bubble; selected state
 * uses a bordered chip. Icons: Kin Chatbot thumb SVGs (``18×18`` artboard).
 * Clicking the active thumb clears feedback (``null``).
 */
export function AgentAssistantThumbsFeedback({
  rating,
  disabled,
  accentColor,
  onRate,
}: AgentAssistantThumbsFeedbackProps) {
  const upOn = rating === 'up';
  const downOn = rating === 'down';
  const idleColor = ADVISOR_PANEL_NEUTRAL_250;
  const hoverSelected = {
    bg: ADVISOR_PANEL_NEUTRAL_50_BG,
    color: accentColor,
    borderWidth: '1px',
    borderColor: accentColor,
  };
  const hoverIdle = {
    bg: ADVISOR_PANEL_NEUTRAL_50_BG,
    color: accentColor,
    borderWidth: '1px',
    borderColor: ADVISOR_PANEL_NEUTRAL_100,
  };
  return (
    <HStack gap={0.5} alignSelf="flex-start" role="group" aria-label="Message feedback">
      <IconButton
        aria-label="Mark as helpful"
        aria-pressed={upOn}
        variant="ghost"
        size="xs"
        minW={ICON_BTN_SIZE}
        minH={ICON_BTN_SIZE}
        w={ICON_BTN_SIZE}
        h={ICON_BTN_SIZE}
        p={0}
        borderRadius="6px"
        borderWidth={upOn ? '1px' : '0'}
        borderColor={upOn ? accentColor : 'transparent'}
        color={upOn ? accentColor : idleColor}
        bg="transparent"
        _hover={disabled ? undefined : upOn ? hoverSelected : hoverIdle}
        _focusVisible={{ boxShadow: `0 0 0 1px ${accentColor}` }}
        _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
        disabled={disabled}
        onClick={() => void onRate(upOn ? null : 'up')}
      >
        <KinThumbUpIcon width={ICON_SIZE} height={ICON_SIZE} aria-hidden focusable={false} />
      </IconButton>
      <IconButton
        aria-label="Mark as not helpful"
        aria-pressed={downOn}
        variant="ghost"
        size="xs"
        minW={ICON_BTN_SIZE}
        minH={ICON_BTN_SIZE}
        w={ICON_BTN_SIZE}
        h={ICON_BTN_SIZE}
        p={0}
        borderRadius="6px"
        borderWidth={downOn ? '1px' : '0'}
        borderColor={downOn ? accentColor : 'transparent'}
        color={downOn ? accentColor : idleColor}
        bg="transparent"
        _hover={disabled ? undefined : downOn ? hoverSelected : hoverIdle}
        _focusVisible={{ boxShadow: `0 0 0 1px ${accentColor}` }}
        _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
        disabled={disabled}
        onClick={() => void onRate(downOn ? null : 'down')}
      >
        <KinThumbDownIcon width={ICON_SIZE} height={ICON_SIZE} aria-hidden focusable={false} />
      </IconButton>
    </HStack>
  );
}
